import {
    FetchResult,
    NextLink,
    Observable,
    Observer,
    Operation,
    ApolloLink,
    ObservableSubscription,
} from '@apollo/client/core';
import type Pusher from 'pusher-js';
import type { Options } from 'pusher-js';
import { getObserver } from '../utils/observers';
import LazyLink, { lazy } from './lazy-loaded-link';

export default class PusherLink extends ApolloLink {
    constructor(private pusher: Pusher) {
        super();
    }

    /**
     * Handles the subscription request.
     */
    request(
        operation: Operation,
        forward?: NextLink
    ): Observable<FetchResult> | null {
        const subscribeObservable = new Observable<FetchResult>(() => {
            return;
        });

        const prevSubscribe =
            subscribeObservable.subscribe.bind(subscribeObservable);

        operation.getContext();

        subscribeObservable.subscribe = ((
            observerOrNext: any,
            onError: any,
            onComplete: any
        ): ObservableSubscription => {
            prevSubscribe(observerOrNext, onError, onComplete);

            const observer = getObserver(observerOrNext, onError, onComplete);

            let subscriptionChannel: string | null;

            if (forward)
                forward(operation).subscribe({
                    next: (data) => {
                        subscriptionChannel =
                            data?.extensions?.lighthouse_subscriptions
                                .channel ?? null;

                        if (!subscriptionChannel) {
                            if (observer.next) observer.next(data);
                            if (observer.complete) observer.complete();
                            return;
                        }

                        this.subscribeToChannel(subscriptionChannel, observer);
                    },
                });

            return {
                closed: false,
                unsubscribe: () => {
                    if (subscriptionChannel) {
                        this.unsubscribeFromChannel(subscriptionChannel);
                    }
                },
            };
        }) as any;

        return subscribeObservable;
    }

    protected subscribeToChannel(
        subscriptionChannel: string,
        observer: Observer<FetchResult>
    ) {
        this.pusher
            .subscribe(subscriptionChannel)
            .bind('lighthouse-subscription', (payload: any) => {
                if (!payload.more) {
                    this.unsubscribeFromChannel(subscriptionChannel);
                    if (observer.complete) observer.complete();
                }

                const result = payload.result;
                if (result && observer.next) observer.next(result);
            });
    }

    protected unsubscribeFromChannel(subscriptionChannel: string) {
        this.pusher.unsubscribe(subscriptionChannel);
    }
}

export function makePusherLink(pusher: Pusher): PusherLink;
export function makePusherLink(
    pusher: () => PromiseLike<Pusher> | Pusher
): LazyLink<PusherLink>;
export function makePusherLink(
    appKey: string,
    options: Options
): LazyLink<PusherLink>;
export function makePusherLink(
    pusher: Pusher | (() => PromiseLike<Pusher> | Pusher) | string,
    options?: Options
): PusherLink | LazyLink<PusherLink> {
    if (typeof pusher === 'string') {
        return lazy(async () => {
            const Pusher = (await import('pusher-js')).default;
            const instance = new Pusher(pusher, options ?? { cluster: 'eu' });
            return new PusherLink(instance);
        });
    }

    if (typeof pusher === 'function') {
        return lazy(async () => new PusherLink(await pusher()));
    }

    return new PusherLink(pusher);
}
